import * as React from 'react';
import {useIsomorphicLayoutEffect} from '@packages/systems/spring-hooks';

import {getSizeClasses} from './getSizeClasses';

const {createContext, useContext} = React;

export type SizeContextSizeValue = 'medium' | 'x-large' | 'x-large-branded';
type SizeContextProps = {
  size: SizeContextSizeValue;
};
type SizeContextProviderProps = {
  children?: React.ReactNode;
  size?: SizeContextSizeValue;
};

const defaultSize = 'medium';
export const SizeContext: React.Context<SizeContextProps> =
  createContext<SizeContextProps>({size: defaultSize});
export const useSizeContext = (): SizeContextProps => useContext(SizeContext);

const SUPPORTED_SIZES = {
  medium: true,
  'x-large': true,
  'x-large-branded': true,
} as const;

const getValidSize = (
  size?: SizeContextSizeValue,
  fallback: SizeContextSizeValue = defaultSize
): SizeContextSizeValue => {
  if (!size) return fallback;

  const isValid = SUPPORTED_SIZES[size];
  return isValid ? size : fallback;
};

/**
 * This nullifies the (DOM) layout effects of the wrapper `div` element
 * for the SizeContextProvider.
 *
 * This style object is cached to avoid unnecessary re-renders.
 */
const stylesToNullifyDOMNode = {display: 'contents'} as const;

/**
 * Added to help render the unified typography tokens at the HTML body.
 * This enables our typography tokens to work for Portal'ed elements in the
 * Designer (e.g. MiniSettings).
 *
 * @see https://webflow.atlassian.net/browse/PDTY-6979
 * 2023-11-15
 */
const unifiedTokensNamespace = 'wf-unified-tokens';

const useSizeTokens = (size: SizeContextSizeValue) => {
  const classes = getSizeClasses({size});

  useIsomorphicLayoutEffect(() => {
    if (!document.body || !classes) return;

    const dataNamespace = `data-${unifiedTokensNamespace}`;
    const didInsertThemeVars = document.body.getAttribute(dataNamespace);

    if (didInsertThemeVars) return;

    document.body.classList.add(classes);
    document.body.setAttribute(dataNamespace, classes);
  }, [classes]);

  return classes;
};

/**
 * Add this to the root of your application or a root of a section in your
 * application (scoped sections are to be used sparringly).
 *
 * This will inform the SizeContext aware components the preferred size
 * to render.
 */
export const SizeContextProvider = ({
  children,
  size,
}: SizeContextProviderProps): React.ReactElement => {
  /**
   * Inherits from a parent SizeContextProvider, ...if it exists.
   * This is needed for Portal'ed instances.
   */
  const inheritedSize = useSizeContext().size;

  /**
   * Order of preference:
   * 1. User specified size (Coming from props).
   * 2. Inherited size (coming from a parent SizeContextProvider).
   * 3. Default size. (Fallback)
   */
  const sizeToUse = size || inheritedSize || defaultSize;
  const sizeValue = getValidSize(sizeToUse);

  const classes = useSizeTokens(sizeValue);
  const contextVal = React.useMemo(() => ({size: sizeValue}), [sizeValue]);

  return (
    <SizeContext.Provider value={contextVal}>
      <div
        className={classes}
        data-wf-size-context={sizeValue}
        style={stylesToNullifyDOMNode}
      >
        {children}
      </div>
    </SizeContext.Provider>
  );
};

/**
 * Determines the correct size for a component within the SizeContext of the
 * theming system.
 *
 * The provided size is preferred (if valid). Otherwise, it will use the
 * value specified by the SizeContext (which hopefully lives somewhere at
 * the root layer of your application).
 *
 * If for some reason neither are available, the default fallback size
 * of 'medium' will be used.
 */
export const useSize = (
  sizeProp?: SizeContextSizeValue
): Exclude<SizeContextSizeValue, 'x-large-branded'> => {
  const {size: fallback} = useSizeContext();
  const validSize = getValidSize(sizeProp, fallback);

  /**
   * We need to coerce x-large-branded to return x-large.
   * The reason for this is that many legacy Spring components were built with size variants.
   * These components do not have an x-large-branded variant.
   * Once we migrate these components to use SizeContext tokens instead of size variants, we can remove this.
   */
  if (validSize === 'x-large-branded') {
    return 'x-large';
  }
  return validSize;
};
